import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';
import type { ExpansionRun } from './types';

/**
 * What a preview is allowed to do beyond its defaults. Every field is optional and omitted from the
 * body when it is: the API's defaults are the contract, and sending `full: false` on every run would
 * make an unasked-for choice look like the client's.
 */
export type ExpansionRunOptions = {
  /** Render every candidate rather than the API's own sample. */
  full?: boolean;
  /** Ask a model for the values nothing else could fill. */
  ai?: boolean;
  /**
   * Only preview products with available stock — in the destination's assigned warehouses, or any
   * Astur warehouse if it has none assigned yet.
   */
  in_stock_only?: boolean;
  /**
   * Only preview products already assigned to the destination and not yet listed there, each placed
   * by its mapping's posting template.
   */
  assigned_only?: boolean;
};

const validate = (subscriptionId: number, destinationSubscriptionId: number, sourceSubscriptionId?: number) => {
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!destinationSubscriptionId || destinationSubscriptionId < 1) {
    throw new Error('Valid destination subscription ID is required');
  }
  if (sourceSubscriptionId !== undefined && (!sourceSubscriptionId || sourceSubscriptionId < 1)) {
    throw new Error('Source subscription ID must be a positive number');
  }
};

type RequestParams = {
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  destinationSubscriptionId: number;
  /**
   * The one channel to preview products from. Omitted previews every channel the organization
   * sells on that is not already on the destination — the API's own default for an org-level run.
   */
  sourceSubscriptionId?: number;
  options?: ExpansionRunOptions;
  /**
   * The signed-in user's display name, recorded as the run's "Requested by". Omitted when there is
   * none, and the API falls back to the token's subject.
   */
  requestedBy?: string;
};

export type CreateRunResponse = {
  run: ExpansionRun;
};

export const createRun = async (
  scope: RequestScope,
  { subscriptionId, destinationSubscriptionId, sourceSubscriptionId, options: runOptions = {}, requestedBy }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<CreateRunResponse>> => {
  try {
    validate(subscriptionId, destinationSubscriptionId, sourceSubscriptionId);

    const requestBody = {
      destination_subscription_id: destinationSubscriptionId,
      // Spread rather than assigned: a key the client did not choose must not appear in the body at
      // all, so the API's own default is what decides.
      ...(sourceSubscriptionId === undefined ? {} : { source_subscription_id: sourceSubscriptionId }),
      ...(runOptions.full === undefined ? {} : { full: runOptions.full }),
      ...(runOptions.ai === undefined ? {} : { ai: runOptions.ai }),
      ...(runOptions.in_stock_only === undefined ? {} : { in_stock_only: runOptions.in_stock_only }),
      ...(runOptions.assigned_only === undefined ? {} : { assigned_only: runOptions.assigned_only }),
      ...(requestedBy ? { requested_by: requestedBy } : {}),
    };

    const result = await scopedChannelApi(scope).post<CreateRunResponse>('atts/expansion/runs', requestBody, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
